import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import OButton from "components/reusable/OButton";

const Authenticator = ({
  isOpen,
  onClose,
  onVerify,
  onSuccess,
  title,
  description,
  allowRecoveryCode = false,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCode("");
      setError("");
      setUseRecoveryCode(false);
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCodeChange = (e) => {
    const raw = e.target.value;
    if (useRecoveryCode) {
      setCode(raw.toUpperCase().slice(0, 9));
    } else {
      setCode(raw.replace(/\D/g, "").slice(0, 6));
    }
    setError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (isLoading) return;

    const minLength = useRecoveryCode ? 8 : 6;
    if (code.length < minLength) {
      setError(
        useRecoveryCode
          ? t("MFA_ENTER_RECOVERY_CODE") || "Please enter your recovery code."
          : t("MFA_ENTER_CODE") || "Please enter the 6-digit code.",
      );
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const result = await onVerify(code, { isRecoveryCode: useRecoveryCode });
      if (result?.verified) {
        onSuccess?.(result);
      } else {
        setError(
          result?.message ||
            t("MFA_INVALID_CODE") ||
            "Invalid code. Please try again.",
        );
        setCode("");
        inputRef.current?.focus();
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("MFA_VERIFY_FAILED") ||
          "Verification failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div
            className="absolute inset-0 bg-gray-500 opacity-75"
            onClick={() => !isLoading && onClose?.()}
          ></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {title ||
                      t("MFA_VERIFY_TITLE") ||
                      "Two-factor verification"}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {description ||
                        (useRecoveryCode
                          ? t("MFA_RECOVERY_DESC") ||
                            "Enter one of your recovery codes."
                          : t("MFA_VERIFY_DESC") ||
                            "Enter the 6-digit code from your authenticator app.")}
                    </p>
                  </div>

                  <div className="mt-4">
                    <input
                      ref={inputRef}
                      type="text"
                      inputMode={useRecoveryCode ? "text" : "numeric"}
                      autoComplete="one-time-code"
                      value={code}
                      onChange={handleCodeChange}
                      disabled={isLoading}
                      placeholder={useRecoveryCode ? "XXXX-XXXX" : "000000"}
                      className="block w-full text-center tracking-[0.4em] text-lg py-3 px-3 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0 focus:border-blue-500 disabled:opacity-60"
                    />
                    {error ? (
                      <p className="mt-2 text-sm text-red-600 text-left">
                        {error}
                      </p>
                    ) : null}
                  </div>

                  {allowRecoveryCode && (
                    <button
                      type="button"
                      className="mt-3 text-sm text-[#6236FF] hover:text-[#9D36FF] hover:underline"
                      onClick={() => {
                        setUseRecoveryCode((v) => !v);
                        setCode("");
                        setError("");
                      }}
                      disabled={isLoading}
                    >
                      {useRecoveryCode
                        ? t("MFA_USE_AUTH_CODE") ||
                          "Use authenticator code instead"
                        : t("MFA_USE_RECOVERY_CODE") ||
                          "Can't access your authenticator? Use a recovery code"}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <OButton
                type="submit"
                label={t("MFA_VERIFY") || "Verify"}
                isLoading={isLoading}
                extraClasses="ml-3 bg-blue-600 hover:bg-blue-700"
              />
              <OButton
                type="button"
                onClick={onClose}
                label={t("CANCEL") || "Cancel"}
                variant="outline"
                disabled={isLoading}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Authenticator;
